// Geometry command builders shared by Select and draw tools (SCP-3 L7: line-work bakes points).
import { normalizeDeg, rotate, type Vec } from "../core/geometry";
import { objectBox } from "./align";
import type { Doc, Entity } from "../doc/entities";
import type { Command } from "../doc/commands";
import { copyEntities, pasteCommand } from "./editOps";
import { carryPivot, pivotOf } from "./pivot";
import { carryPipe } from "../pipes/carry";

type Patch = { id: string; before: Record<string, unknown>; after: Record<string, unknown> };

function translated(entity: Entity, d: Vec): Patch | null {
  const mv = (p: Vec): Vec => ({ x: p.x + d.x, y: p.y + d.y });
  switch (entity.type) {
    case "polyline":
    case "marker":
      return {
        id: entity.id,
        before: { points: entity.points },
        after: { points: entity.points.map(mv) }
      };
    case "circle":
    case "arc":
      return { id: entity.id, before: { center: entity.center }, after: { center: mv(entity.center) } };
    case "text":
      return { id: entity.id, before: { pos: entity.pos }, after: { pos: mv(entity.pos) } };
    case "label":
      return {
        id: entity.id,
        before: { head: entity.head, tail: entity.tail },
        after: { head: mv(entity.head), tail: mv(entity.tail) }
      };
    case "image":
    case "grid":
      return { id: entity.id, before: { pos: entity.pos }, after: { pos: mv(entity.pos) } };
    case "dimension": {
      // entity anchors follow their geometry; only FREE anchors translate
      if (entity.a.kind !== "free" && entity.b.kind !== "free") return null;
      const move = (anchor: typeof entity.a) =>
        anchor.kind === "free" ? { kind: "free" as const, p: mv(anchor.p) } : structuredClone(anchor);
      return {
        id: entity.id,
        before: { a: structuredClone(entity.a), b: structuredClone(entity.b) },
        after: { a: move(entity.a), b: move(entity.b) }
      };
    }
    case "group":
      return null; // group children translate individually
  }
}

/** Patches translating `ids` (expanding group children) by delta. */
export function translatePatches(doc: Doc, ids: Iterable<string>, d: Vec): Patch[] {
  const out: Patch[] = [];
  const mv = (p: Vec): Vec => ({ x: p.x + d.x, y: p.y + d.y });
  const list = [...ids];
  const moving = expandGroupChildren(doc, list); // pipe ends attached to a moving owner move with it
  const visit = (id: string): void => {
    const entity = doc.entities[id];
    if (!entity) return;
    if (entity.type === "group") {
      // the group's turning point, fitting ports and free pipe ends move with it
      const gp = carryPipe(entity, carryPivot(entity, null, mv), mv, moving);
      if (gp) out.push(gp);
      entity.children.forEach(visit);
      return;
    }
    const p = carryPivot(entity, translated(entity, d), mv);
    if (p) out.push(p);
  };
  for (const id of list) visit(id);
  return out;
}

export function moveCommand(doc: Doc, ids: Iterable<string>, d: Vec): Command {
  return { kind: "updateEntities", patches: translatePatches(doc, ids, d) };
}

/** TL-5 Ctrl-drag copy / Ctrl+D: clones at +delta with fresh ids, keeping layers and groups. */
export function duplicateCommand(
  doc: Doc,
  ids: Iterable<string>,
  d: Vec
): { cmd: Command; newIds: string[] } | null {
  return pasteCommand(copyEntities(doc, ids), null, d);
}

/** IMG-10 whole-drawing calibrate: scale every entity about the drawing centroid. */
export function scaleAllPatches(doc: Doc, factor: number): Patch[] {
  // centroid from entity geometry (cheap: average of translation reference points)
  const refs: Vec[] = [];
  for (const e of Object.values(doc.entities)) {
    if (e.type === "polyline" || e.type === "marker") refs.push(...e.points);
    else if (e.type === "circle" || e.type === "arc") refs.push(e.center);
    else if (e.type === "text" || e.type === "image" || e.type === "grid") refs.push(e.pos);
    else if (e.type === "label") refs.push(e.head, e.tail);
  }
  if (refs.length === 0) return [];
  const c: Vec = {
    x: refs.reduce((s, p) => s + p.x, 0) / refs.length,
    y: refs.reduce((s, p) => s + p.y, 0) / refs.length
  };
  const sc = (p: Vec): Vec => ({ x: c.x + (p.x - c.x) * factor, y: c.y + (p.y - c.y) * factor });
  const out: Patch[] = [];
  for (const e of Object.values(doc.entities)) {
    switch (e.type) {
      case "polyline":
      case "marker":
        out.push({ id: e.id, before: { points: e.points }, after: { points: e.points.map(sc) } });
        break;
      case "circle":
      case "arc":
        out.push({
          id: e.id,
          before: { center: e.center, r: e.r },
          after: { center: sc(e.center), r: e.r * factor }
        });
        break;
      case "image":
      case "grid":
        out.push({
          id: e.id,
          before:
            e.type === "image"
              ? { pos: e.pos, wMm: e.wMm, hMm: e.hMm }
              : { pos: e.pos, cellW: e.cellW, cellH: e.cellH },
          after:
            e.type === "image"
              ? { pos: sc(e.pos), wMm: e.wMm * factor, hMm: e.hMm * factor }
              : { pos: sc(e.pos), cellW: e.cellW * factor, cellH: e.cellH * factor }
        });
        break;
      case "text":
        out.push({
          id: e.id,
          before: { pos: e.pos, textStyle: e.textStyle },
          after: { pos: sc(e.pos), textStyle: { ...e.textStyle, size: e.textStyle.size * factor } }
        });
        break;
      case "label":
        out.push({ id: e.id, before: { head: e.head, tail: e.tail }, after: { head: sc(e.head), tail: sc(e.tail) } });
        break;
      case "dimension":
        break; // dimensions follow anchors
      case "group": {
        // groups have no geometry, but carry a turning point, fitting ports and free pipe ends
        const gp = carryPipe(e, carryPivot(e, null, sc), sc);
        if (gp) out.push(gp);
        break;
      }
    }
  }
  return out;
}

/** The turn an object carries, in degrees (clockwise on screen). Placed things store it; line-work remembers it in meta.rot. */
export function rotationOf(entity: Entity): number {
  if (entity.type === "text" || entity.type === "image" || entity.type === "grid") return entity.rotationDeg;
  const r = entity.meta?.rot;
  return typeof r === "number" && Number.isFinite(r) ? r : 0;
}

/** meta patch that adds `deg` to the remembered turn (nothing when the object did not turn). */
function rotMeta(entity: Entity, deg: number): { meta?: Record<string, unknown> } {
  if (deg === 0) return {};
  const next = Math.round(normalizeDeg(rotationOf(entity) + deg) * 1e6) / 1e6;
  return { meta: { ...(entity.meta ?? {}), rot: next } };
}

/**
 * One entity under a rigid rotation (deg, CW+ screen) and/or uniform scale (factor)
 * about a shared pivot, expressed by the point map `mp`. Baked-geometry types map
 * their points; placed types (text/image/grid) orbit their own pivot and add the
 * angle; sized values (r, wMm, font size, dim offset) multiply by the factor.
 */
function transformed(entity: Entity, mp: (p: Vec) => Vec, factor: number, deg: number): Patch | null {
  switch (entity.type) {
    case "marker":
      // note: cloud markers store 2 bbox corners — rotation re-derives an axis-aligned
      // cloud between the mapped corners (clouds have no rotation of their own)
      return { id: entity.id, before: { points: entity.points }, after: { points: entity.points.map(mp) } };
    case "polyline":
      // line-work bakes its points, so the turn is remembered in meta.rot (the Inspector shows it)
      return {
        id: entity.id,
        before: { points: entity.points, ...(deg !== 0 ? { meta: entity.meta } : {}) },
        after: { points: entity.points.map(mp), ...rotMeta(entity, deg) }
      };
    case "circle":
      return {
        id: entity.id,
        before: { center: entity.center, r: entity.r },
        after: { center: mp(entity.center), r: entity.r * factor }
      };
    case "arc":
      return {
        id: entity.id,
        before: { center: entity.center, r: entity.r, startDeg: entity.startDeg, ...(deg !== 0 ? { meta: entity.meta } : {}) },
        after: {
          center: mp(entity.center),
          r: entity.r * factor,
          startDeg: normalizeDeg(entity.startDeg + deg),
          ...rotMeta(entity, deg)
        }
      };
    case "text":
      return {
        id: entity.id,
        before: { pos: entity.pos, rotationDeg: entity.rotationDeg, textStyle: entity.textStyle },
        after: {
          pos: mp(entity.pos),
          rotationDeg: normalizeDeg(entity.rotationDeg + deg),
          textStyle: { ...entity.textStyle, size: entity.textStyle.size * factor }
        }
      };
    case "label":
      return {
        id: entity.id,
        before: { head: entity.head, tail: entity.tail, textStyle: entity.textStyle },
        after: {
          head: mp(entity.head),
          tail: mp(entity.tail),
          textStyle: { ...entity.textStyle, size: entity.textStyle.size * factor }
        }
      };
    case "image": {
      // image rotation pivots on its own centre (imageCorners) — orbit the centre
      const c = { x: entity.pos.x + entity.wMm / 2, y: entity.pos.y + entity.hMm / 2 };
      const nc = mp(c);
      const w = entity.wMm * factor;
      const h = entity.hMm * factor;
      return {
        id: entity.id,
        before: { pos: entity.pos, rotationDeg: entity.rotationDeg, wMm: entity.wMm, hMm: entity.hMm },
        after: {
          pos: { x: nc.x - w / 2, y: nc.y - h / 2 },
          rotationDeg: normalizeDeg(entity.rotationDeg + deg),
          wMm: w,
          hMm: h
        }
      };
    }
    case "grid":
      return {
        id: entity.id,
        before: { pos: entity.pos, rotationDeg: entity.rotationDeg, cellW: entity.cellW, cellH: entity.cellH },
        after: {
          pos: mp(entity.pos),
          rotationDeg: normalizeDeg(entity.rotationDeg + deg),
          cellW: entity.cellW * factor,
          cellH: entity.cellH * factor
        }
      };
    case "dimension": {
      // entity anchors follow their geometry; only free anchors move, offset scales
      if (entity.a.kind !== "free" && entity.b.kind !== "free" && factor === 1) return null;
      const map = (anchor: typeof entity.a) =>
        anchor.kind === "free" ? { kind: "free" as const, p: mp(anchor.p) } : structuredClone(anchor);
      return {
        id: entity.id,
        before: { a: structuredClone(entity.a), b: structuredClone(entity.b), offset: entity.offset },
        after: { a: map(entity.a), b: map(entity.b), offset: entity.offset * factor }
      };
    }
    case "group":
      return null; // children transform individually
  }
}

function transformPatches(
  doc: Doc,
  ids: Iterable<string>,
  mp: (p: Vec) => Vec,
  factor: number,
  deg: number
): Patch[] {
  const out: Patch[] = [];
  const list = [...ids];
  const moving = expandGroupChildren(doc, list);
  const visit = (id: string): void => {
    const entity = doc.entities[id];
    if (!entity) return;
    if (entity.type === "group") {
      const gp = carryPipe(
        entity,
        carryPivot(
          entity,
          deg !== 0 ? { id: entity.id, before: { meta: entity.meta }, after: rotMeta(entity, deg) as Record<string, unknown> } : null,
          mp
        ),
        mp,
        moving
      );
      if (gp) out.push(gp);
      entity.children.forEach(visit);
      return;
    }
    const p = carryPivot(entity, transformed(entity, mp, factor, deg), mp);
    if (p) out.push(p);
  };
  for (const id of list) visit(id);
  return out;
}

/** Patches rotating `ids` (groups expanded) about `pivot` by `deg` (CW+ on screen). */
export function rotatePatches(doc: Doc, ids: Iterable<string>, pivot: Vec, deg: number): Patch[] {
  return transformPatches(doc, ids, (p) => rotate(p, pivot, deg), 1, deg);
}

/**
 * Make one object's turn equal `target` degrees (the Inspector "Rotation" box). An object with a
 * set turning point turns about that point. Otherwise text, pictures and grids store their turn
 * and keep their own pivot, and line-work and groups turn about the centre of their box by the
 * difference, so typing 0 returns them to the way they were drawn or placed.
 */
export function setRotationPatches(doc: Doc, id: string, target: number): Patch[] {
  const e = doc.entities[id];
  if (!e || !Number.isFinite(target)) return [];
  const want = normalizeDeg(target);
  const own = pivotOf(e);
  if (own) {
    let turn = want - normalizeDeg(rotationOf(e));
    if (turn > 180) turn -= 360;
    if (turn <= -180) turn += 360;
    return Math.abs(turn) < 1e-9 ? [] : rotatePatches(doc, [id], own, turn);
  }
  if (e.type === "text" || e.type === "image" || e.type === "grid") {
    return Math.abs(normalizeDeg(e.rotationDeg) - want) < 1e-9 ? [] : [{ id, before: { rotationDeg: e.rotationDeg }, after: { rotationDeg: want } }];
  }
  let delta = want - normalizeDeg(rotationOf(e));
  if (delta > 180) delta -= 360;
  if (delta <= -180) delta += 360;
  if (Math.abs(delta) < 1e-9) return [];
  const box = objectBox(doc, id);
  if (!box) return [];
  return rotatePatches(doc, [id], { x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2 }, delta);
}

/** Patches uniformly scaling `ids` (groups expanded) about `pivot` by `factor` (> 0). */
export function scalePatches(doc: Doc, ids: Iterable<string>, pivot: Vec, factor: number): Patch[] {
  const sc = (p: Vec): Vec => ({ x: pivot.x + (p.x - pivot.x) * factor, y: pivot.y + (p.y - pivot.y) * factor });
  return transformPatches(doc, ids, sc, factor, 0);
}

/** Selection ids + all group descendants (for editing-set/snap exclusion during moves). */
export function expandGroupChildren(doc: Doc, ids: Iterable<string>): Set<string> {
  const out = new Set<string>();
  const visit = (id: string): void => {
    if (out.has(id)) return;
    out.add(id);
    const entity = doc.entities[id];
    if (entity?.type === "group") entity.children.forEach(visit);
  };
  for (const id of ids) visit(id);
  return out;
}

/** Effective locked state (entity or its layer) — EC-TL-15. */
export function isLocked(doc: Doc, id: string): boolean {
  const entity = doc.entities[id];
  if (!entity) return false;
  if (entity.locked) return true;
  return doc.layers.find((l) => l.id === entity.layerId)?.locked ?? false;
}
